/**
 * ScormSubmitModal — Pure HTML/CSS confirmation modal for SCORM result submission.
 * Used by both ScormPreview (stub APIs) and ScormPlayerFrame (real adapters).
 * No external UI framework dependencies.
 */

import React from 'react';
import type { ScormTranslations } from './i18n';

export interface ScormSubmitModalData {
    /** Lesson/QCO status (e.g. 'completed', 'attempted'). */
    status?: string;
    /** Time in milliseconds. */
    timeMs?: number;
    /** Numeric score. */
    score?: number;
}

export interface ScormSubmitModalProps {
    /** Data to display in the modal. */
    data: ScormSubmitModalData;
    /** i18n translations. */
    t: ScormTranslations;
    /** Called when user confirms submission. */
    onConfirm: () => void;
    /** Called when user cancels. */
    onCancel: () => void;
}

export function ScormSubmitModal({ data, t, onConfirm, onCancel }: ScormSubmitModalProps) {
    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 10000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.45)',
                animation: 'scorm-modal-fade-in 0.2s ease',
            }}
            onClick={onCancel}
        >
            <div
                style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.1)',
                    padding: '24px 28px',
                    minWidth: 360,
                    maxWidth: 440,
                    animation: 'scorm-modal-scale-in 0.2s ease',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                    <div style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 18, color: '#fff', flexShrink: 0,
                    }}>✓</div>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: 16, color: '#111827' }}>
                            {t.submitConfirmTitle}
                        </div>
                        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                            {t.submitConfirmMessage}
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            padding: '8px 18px',
                            borderRadius: 8,
                            border: '1px solid #d1d5db',
                            backgroundColor: '#fff',
                            color: '#374151',
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#f3f4f6'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = '#fff'; }}
                    >
                        {t.submitConfirmCancel}
                    </button>
                    <button
                        type="button"
                        onClick={onConfirm}
                        style={{
                            padding: '8px 22px',
                            borderRadius: 8,
                            border: 'none',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            color: '#fff',
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                            transition: 'all 0.15s',
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                            e.currentTarget.style.transform = 'translateY(0)';
                        }}
                    >
                        {t.submitConfirmSubmit}
                    </button>
                </div>
            </div>

            {/* CSS animations */}
            <style>{`
                @keyframes scorm-modal-fade-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes scorm-modal-scale-in {
                    from { opacity: 0; transform: scale(0.95); }
                    to { opacity: 1; transform: scale(1); }
                }
            `}</style>
        </div>
    );
}

/** Green submitted banner shown after successful submission. */
export function ScormSubmittedBanner({ t }: { t: ScormTranslations }) {
    return (
        <div style={{
            padding: '8px 14px',
            backgroundColor: '#ecfdf5',
            borderTop: '1px solid #a7f3d0',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
            color: '#065f46',
            fontWeight: 500,
            flexShrink: 0,
        }}>
            <span style={{ fontSize: 14 }}>✓</span>
            <span>{t.submitConfirmSubmitted}</span>
        </div>
    );
}
